// The PDI Holds view's reading of a boat: how far its checklist has got, which sections still need
// work and which items in them are open, plus the one-tap Status change applied to the cached list so a
// tap shows at once (the sheet's own Tracker / automations catch up in Smartsheet).

import type { PdiCategory, PdiHold, PdiItem, PdiVessel } from '@technohull/shared';

/** a row counts as checked once it reads Yes or No: the sheet's Tracker counts the same two. */
export const pdiChecked = (status: string): boolean => status === 'Yes' || status === 'No';

export interface PdiSectionView {
  cat: PdiCategory;
  total: number;
  checked: number;
  /** items not checked yet (blank or Hold), in sheet order */
  open: PdiItem[];
  /** items on Hold */
  held: number;
  /** the section row itself reads Hold */
  onHold: boolean;
  /** nothing left to do: every item checked and the section not on hold */
  done: boolean;
}

export function pdiSection(cat: PdiCategory): PdiSectionView {
  const open = cat.items.filter((it) => !pdiChecked(it.status));
  const held = cat.items.filter((it) => it.status === 'Hold').length;
  const onHold = cat.status === 'Hold';
  return {
    cat,
    total: cat.items.length,
    checked: cat.items.length - open.length,
    open,
    held,
    onHold,
    done: open.length === 0 && !onHold,
  };
}

/** the boat's checklist as a whole: items checked of all its items */
export function pdiProgress(v: PdiVessel): { checked: number; total: number } {
  let checked = 0;
  let total = 0;
  for (const c of v.categories) {
    total += c.items.length;
    checked += c.items.filter((it) => pdiChecked(it.status)).length;
  }
  return { checked, total };
}

/** holds that are neither a section nor an item of one (an item row above the boat's first section),
 *  so the Holds view still lists them */
export function pdiLooseHolds(v: PdiVessel): PdiHold[] {
  const known = new Set<number>();
  for (const c of v.categories) {
    known.add(c.rowId);
    for (const it of c.items) known.add(it.rowId);
  }
  return v.holds.filter((h) => !known.has(h.rowId));
}

/** a boat still on PDI: the boat reads Hold or something on it does */
export const pdiOnHold = (v: PdiVessel): boolean => v.status === 'Hold' || v.holds.length > 0;
/** a boat that has passed: Yes, with nothing on hold */
export const pdiPassed = (v: PdiVessel): boolean => v.status === 'Yes' && v.holds.length === 0;

/** the season's PDI list with one row's Status changed, as the sheet will read once it is saved: the boat,
 *  section or item, and the boat's holds (one added when a row goes on Hold, dropped when it comes off).
 *  Checking a section's last open item also clears the section, as the sheet's automation does
 *  (Tracker 1 → Yes). Rows it doesn't find leave the list as it was (the same array). */
export function withPdiStatus(vessels: PdiVessel[], sheetId: string, rowId: number, status: string): PdiVessel[] {
  let hit = false;
  const out = vessels.map((v) => {
    if (v.sheetId !== sheetId) return v;
    if (v.rowId === rowId) {
      hit = true;
      return { ...v, status: (status === 'Hold' || status === 'Yes' ? status : '') as PdiVessel['status'] };
    }
    const owner = v.categories.find((c) => c.rowId === rowId || c.items.some((it) => it.rowId === rowId));
    if (!owner) return v;
    hit = true;
    let holds = v.holds;
    const setHold = (row: number, name: string, comment: string, on: boolean, tracker: string) => {
      const at = holds.findIndex((h) => h.rowId === row);
      if (on && at < 0) holds = [...holds, { category: name, comment, status: 'Hold', rowId: row, tracker }];
      else if (!on && at >= 0) holds = holds.filter((h) => h.rowId !== row);
    };
    const categories = v.categories.map((c) => {
      if (c !== owner) return c;
      if (c.rowId === rowId) {
        const s = pdiSection(c);
        setHold(c.rowId, c.name, c.comment, status === 'Hold', `${s.checked}/${s.total}`);
        return { ...c, status };
      }
      const items = c.items.map((it) => (it.rowId === rowId ? { ...it, status } : it));
      const it = items.find((x) => x.rowId === rowId)!;
      setHold(it.rowId, it.name, it.comment, status === 'Hold', '');
      let next: PdiCategory = { ...c, items };
      const s = pdiSection(next);
      if (s.checked === s.total && s.total > 0 && c.status === 'Hold') {
        next = { ...next, status: 'Yes' };
        setHold(c.rowId, c.name, c.comment, false, '');
      } else if (c.status === 'Hold') {
        // the section's hold reads its Tracker ("3/9"), which moves with every item
        holds = holds.map((h) => (h.rowId === c.rowId ? { ...h, tracker: `${s.checked}/${s.total}` } : h));
      }
      return next;
    });
    return { ...v, categories, holds };
  });
  return hit ? out : vessels;
}
